import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import Swal from 'sweetalert2';
import { ICarrier } from 'src/app/models/ICarrier.model';
import { IWorker } from 'src/app/models/IWorker.model';
import { Orders } from 'src/app/models/Orders.model';
import { Priority } from 'src/app/models/Priority.model';
import { Products } from 'src/app/models/Product.model';
import { IWsResult } from 'src/app/models/ws_result.model';
import { OrdersService } from 'src/app/services/orders.service';
import { DropdownService } from 'src/app/services/shared/dropdown.service';
import { OpenModalService } from 'src/app/services/shared/open-modal.service';

/**
 * OrderscmDet — assign-order modal opened from `ordini-pm` (PM /
 * custom-made order list). Two panes:
 *   • LEFT  — order detail + assign form (number, date, customer,
 *             operator combobox, priority combobox, shipping
 *             address, carrier combobox, note).
 *   • RIGHT — products list with per-row checkbox + qty input.
 * Confirm button posts the whole payload via
 * `OrdersService.addOrder(...)`.
 *
 * Phase 1.5 / v2.6.0 — TS cleaned up:
 *   • dropped ~30 `console.log` debug lines,
 *   • dropped ~12 dead fields (boxes, dropProducts, productToAdd,
 *     productsToAddSolo, productsToSend, vsDetails, orderToUpdate,
 *     boxes / box-management state — none of these are touched by
 *     the new template, which only shows assign + products),
 *   • dropped ~15 dead methods (box add/remove, similar-product
 *     search, addProduct(Solo), removeProduct, save() with edit
 *     form, addDocumentsToBox / addScrewsToBox / printAllLabels,
 *     etc.),
 *   • dropped `orderDetailsForm` (the "edit order" form that the
 *     template never wires up — only the assign form is used).
 *
 * Behaviour preserved 1:1: assign flow, partial-order detection,
 * select-all / deselect-all, qty-picked update.
 */
@Component({
  selector: 'app-orderscm-det',
  templateUrl: './orderscm-det.component.html',
  styleUrls: ['./orderscm-det.component.scss'],
  standalone: false,
})


export class OrderscmDetComponent implements OnInit {

  private readonly ordersServ = inject(OrdersService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly dropdownServ = inject(DropdownService);
  private readonly openModal = inject(OpenModalService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  @Input() opened: boolean = false;
  @Input() order_id!: string;

  @Output() pingBackForBoolean = new EventEmitter<boolean>();

  // ── Data ──────────────────────────────────────────────────────────
  public orderNew: any = null;
  public productsNew: Products.IProductToSend[] = [];
  public assignOrderForm!: FormGroup;
  public priorityOptions: Priority.IPriority[] = [];
  public carriers: ICarrier[] = [];
  public workers: IWorker[] = [];

  // ── UI state ─────────────────────────────────────────────────────
  public loading = false;
  public areSelected = false;
  public partialOrder: string | null = null;

  // ──────────────────────────────────────────────────────────────────
  // Lifecycle.
  // ──────────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.assignOrderForm = this.formBuilder.group({
      or_order_number: [''],
      or_order_date: [''],
      or_order_priority_cod: ['', Validators.required],
      carrier_id: ['', Validators.required],
      or_customer: [''],
      or_shipping_address: [''],
      or_shipping_zip: [''],
      or_shipping_city: [''],
      or_shipping_prov: [''],
      or_shipping_country: [''],
      or_assigned_id: ['', Validators.required],
      or_note: [''],
    });
    this.loadOrder();
  }

  // ──────────────────────────────────────────────────────────────────
  // Data loader.
  // ──────────────────────────────────────────────────────────────────

  private loadOrder(): void {
    if (!this.order_id) return;
    this.ordersServ
      .getNewOrder(this.order_id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (orderData: IWsResult) => {
          this.orderNew = orderData?.data ?? null;
          if (!this.orderNew) return;

          // Build the per-row products view-model.
          this.productsNew = (this.orderNew.products ?? []).map((item: any): Products.IProductToSend => ({
            op_note: '',
            op_checked: false,
            op_code: item.op_code,
            op_description: item.op_description,
            op_qta: item.op_qta,
            op_weigth: item.op_weigth,
            op_stock: item.op_stock,
            op_qta_picked: +item.op_qta,
            op_qta_ordered: item.op_qta_ordered,
            op_positions: [],
            tab_order_status_cod: [],
          }));

          this.assignOrderForm.patchValue({
            or_order_number: this.orderNew.or_order_number,
            or_order_date: this.orderNew.or_order_date,
            carrier_id: this.orderNew?.or_carrier_id,
            or_customer: this.orderNew.or_customer,
            or_shipping_address: this.orderNew.or_shipping_address,
            or_shipping_zip: this.orderNew.or_shipping_zip,
            or_shipping_city: this.orderNew.or_shipping_city,
            or_shipping_prov: this.orderNew.or_shipping_prov,
            or_shipping_country: this.orderNew.or_shipping_country,
            or_note: this.orderNew.or_note,
          });

          this.recomputePartialOrder();
          this.cdr.detectChanges();
        },
        error: () => this.cdr.detectChanges(),
      });

    // Dropdowns can load in parallel.
    this.dropdownServ
      .getPriorities()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((priorityData) => {
        this.priorityOptions = priorityData ?? [];
        this.cdr.detectChanges();
      });

    this.dropdownServ
      .getCarriers()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((carriersData) => {
        this.carriers = carriersData ?? [];
        this.cdr.detectChanges();
      });

    this.dropdownServ
      .getWorkers()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((workersData) => {
        (workersData ?? []).forEach(
          (w) => (w.full_name = `${w.ac_first_name} ${w.ac_last_name}`),
        );
        this.workers = workersData ?? [];
        this.cdr.detectChanges();
      });
  }

  // ──────────────────────────────────────────────────────────────────
  // Products list.
  // ──────────────────────────────────────────────────────────────────

  selectAllProducts(): void {
    this.areSelected = !this.areSelected;
    this.productsNew.forEach((item) => (item.op_checked = this.areSelected));
    this.recomputePartialOrder();
  }

  opChecked(_product: Products.IProductToSend, index: number): void {
    this.productsNew[index].op_checked = !this.productsNew[index].op_checked;
    this.recomputePartialOrder();
  }

  updateProduct(value: any, i: number): void {
    this.productsNew[i].op_qta_picked = Number(value);
    this.productsNew[i].op_checked = true;
    this.recomputePartialOrder();
  }

  private recomputePartialOrder(): void {
    const allChecked = this.productsNew.every((p) => p.op_checked === true);
    const allFull = this.productsNew.every(
      (p) => +p.op_qta === p.op_qta_picked,
    );
    this.partialOrder = allChecked && allFull ? 'N' : 'S';
  }

  // ──────────────────────────────────────────────────────────────────
  // Submit.
  // ──────────────────────────────────────────────────────────────────

  addOrderMethod(_form: any): void {
    if (this.assignOrderForm.invalid || !this.orderNew) return;
    this.loading = true;

    const assignOrder: Orders.IOrderToSend = {
      or_assignment_date: this.orderNew.or_order_date,
      or_assigned_id: this.assignOrderForm.value.or_assigned_id,
      or_order_priority_cod: this.assignOrderForm.value.or_order_priority_cod,
      or_carrier_id: this.assignOrderForm.value.carrier_id,
      or_partial: this.partialOrder ?? 'S',
      or_customer_reference: '123',
      products: this.productsNew.map((item) => {
        item.op_qta = item.op_qta_picked;
        return item;
      }),
      or_order_number: this.assignOrderForm.value.or_order_number,
      or_order_date: this.orderNew.or_order_date,
      or_customer: this.assignOrderForm.value.or_customer,
      or_shipping_address: this.assignOrderForm.value.or_shipping_address,
      or_shipping_city: this.assignOrderForm.value.or_shipping_city,
      or_shipping_prov: this.assignOrderForm.value.or_shipping_prov,
      or_shipping_country: this.assignOrderForm.value.or_shipping_country,
      or_gamma_id: this.orderNew.or_gamma_id,
      or_agent_id: '1',
      or_customer_code: this.orderNew.or_customer_code,
      or_customer_mail: this.orderNew.or_customer_mail,
      or_customer_phone: this.orderNew.or_customer_phone,
      or_shipping_zip: this.assignOrderForm.value.or_shipping_zip,
      or_quote_number: this.orderNew.or_quote_number,
      or_quote_date: this.orderNew.or_quote_date,
      or_subject: this.orderNew.or_subject,
      or_note: this.assignOrderForm.value.or_note,
      or_boxes: this.orderNew.or_boxes,
      or_shipping_customer: this.orderNew.or_shipping_customer,
      or_carrier_desc: this.orderNew.or_carrier_desc,
    };

    this.ordersServ
      .addOrder(assignOrder)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (resp) => {
          this.loading = false;
          if (resp?.success) {
            this.goBack();
            this.pingBackForBoolean.emit(true);
            Swal.fire({
              icon: 'success',
              title: 'Assegnato!',
              text: 'Ordine assegnato con successo!',
              timer: 1500,
              timerProgressBar: true,
              showConfirmButton: false,
            });
          } 
          else {
            Swal.fire({
              icon: 'error',
              title: 'Non assegnato',
              text:
                resp?.error_message?.msg_text ||
                'Si è verificato un problema, riprova.',
            });
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.loading = false;
          Swal.fire({
            icon: 'error',
            title: 'Errore',
            text: 'Errore durante l’assegnazione.',
          });
          this.cdr.detectChanges();
        },
      });
  }

  // ──────────────────────────────────────────────────────────────────
  // Close.
  // ──────────────────────────────────────────────────────────────────

  goBack(): void {
    this.openModal.offStorico();
  }

  close(): void {
    this.goBack();
  }
  
}
